/**
 * Maps a Dante config onto the shared Router component: every tx channel is a
 * source, every rx channel a target, grouped by device. Port ids are
 * `device/index`, so renaming a channel never changes a route.
 */

import {
  danteChannelKey,
  danteChannelName,
  parseDanteChannelKey,
  type DanteConfig,
  type DanteNetworkState,
  type DanteRouteState,
  type DanteSource,
} from "@production-hub/shared";
import type { RouterPort, RouteState } from "@/components/router";

const UNRESOLVED_PREFIX = "unresolved:";

export interface DanteRouterModel {
  sources: RouterPort[];
  targets: RouterPort[];
  routes: Record<string, string | null>;
  routeState: Record<string, RouteState>;
  routeFallbackLabel: Record<string, string>;
}

const ROUTE_STATE: Record<DanteRouteState, RouteState> = {
  connected: "ok",
  pending: "pending",
  unresolved: "unresolved",
  device_offline: "pending",
  error: "error",
};

export function buildDanteRouterModel(
  config: DanteConfig,
  live?: DanteNetworkState,
): DanteRouterModel {
  const isOffline = (device: string) =>
    !!live && live.devices[device]?.online === false;
  const model: DanteRouterModel = {
    sources: [],
    targets: [],
    routes: {},
    routeState: {},
    routeFallbackLabel: {},
  };

  for (const device of config.devices) {
    const disabled = isOffline(device.name);
    for (const ch of device.tx) {
      model.sources.push({
        id: danteChannelKey(device.name, ch.index),
        label: danteChannelName(ch),
        defaultLabel: ch.default_name,
        group: device.name,
        disabled,
      });
    }
    for (const ch of device.rx) {
      const id = danteChannelKey(device.name, ch.index);
      model.targets.push({
        id,
        label: danteChannelName(ch),
        defaultLabel: ch.default_name,
        group: device.name,
        disabled,
      });
      if (!ch.source) {
        model.routes[id] = null;
        continue;
      }
      if ("unresolved_name" in ch.source) {
        model.routes[id] =
          `${UNRESOLVED_PREFIX}${ch.source.unresolved_name}@${ch.source.device}`;
        model.routeFallbackLabel[id] =
          `${ch.source.unresolved_name}@${ch.source.device}`;
      } else {
        model.routes[id] = danteChannelKey(ch.source.device, ch.source.index);
      }
      const status = live?.routes[id];
      if (status) model.routeState[id] = ROUTE_STATE[status.state];
      else if ("unresolved_name" in ch.source) {
        model.routeState[id] = "unresolved";
      }
    }
  }
  return model;
}

/** Router source id → Dante source (null for an unresolved name) */
export function danteSourceFromPortId(sourceId: string): DanteSource | null {
  if (sourceId.startsWith(UNRESOLVED_PREFIX)) return null;
  const parsed = parseDanteChannelKey(sourceId);
  return parsed ? { device: parsed.device, index: parsed.index } : null;
}
